import { Flex, Image, Stack, Text } from '@mantine/core'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import { IconButton } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import {
	getChapterDetails,
	parseChapterDetails,
	updatePublishedChapter,
} from 'src/apis/bookAPI'
import { uploadDocFile, uploadProgress } from 'src/apis/uploadApi'
import DocFileCard from 'src/components/BookUniVini/DocFileCard'
import {
	WordFileRefusalAlert,
	WordFileReplaceNotice,
} from 'src/components/BookUniVini/WordFileNotices'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import CustomInput from 'src/components/CustomComponents/CustomInput'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import { useModal } from 'src/context/ModalContext'
import { BookData, ChapterData } from 'src/interfaces'
import {
	DOCX_DROPZONE_ACCEPT,
	pickDocWordFiles,
} from 'src/utils/docxDropzone'
import { getBookCoverImageUrl, getBookTitle } from 'src/utils/bookCoverImage'
import {
	TextFixRefusal,
	describeTextFixResult,
	getChapterWordFile,
	getTextFixRefusal,
	getTextFixResult,
	isTextFixPartial,
} from 'src/utils/chapterWordFile'

interface ChapterInfoEditDrawerProps {
	opened: boolean
	onClose: () => void
	book: BookData | null
	chapter: ChapterData | null
	onRefresh?: () => void | Promise<void>
}

type ChapterInfoForm = {
	title: string
	summary: string
	thumbnail: string
	docUrl: string
}

const TITLE_MAX = 45
const SUMMARY_MAX = 500

const getEnglishDocUrl = (chapter?: ChapterData | null) => {
	if (!chapter) return ''

	// UD-614: the chapter's own field is the file in use. The copy inside `content` could stay
	// on an older file after a replace; read first, it was sent back on the next Save and the
	// server took it for a new file.
	return (
		chapter.original_doc_url ||
		chapter.content?.find((item) => item.language === 'en')?.docUrl ||
		''
	)
}

const buildFormFromChapter = (chapter?: ChapterData | null): ChapterInfoForm => ({
	title: chapter?.title || '',
	summary: chapter?.summary || chapter?.description || '',
	thumbnail: chapter?.cover_image || '',
	docUrl: getEnglishDocUrl(chapter),
})

function ChapterInfoEditDrawer({
	opened,
	onClose,
	book,
	chapter,
	onRefresh,
}: ChapterInfoEditDrawerProps) {
	const { openError, openModal } = useModal()
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [form, setForm] = useState<ChapterInfoForm>(buildFormFromChapter(chapter))
	const [baselineForm, setBaselineForm] = useState<ChapterInfoForm>(
		buildFormFromChapter(chapter),
	)
	const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
	const [thumbnailProgress, setThumbnailProgress] = useState(0)
	const [docProgress, setDocProgress] = useState(0)
	// UD-614: the name of a Word file picked in this drawer, and the chapter as last read
	const [uploadedDoc, setUploadedDoc] = useState<{ url: string; name: string } | null>(null)
	const [details, setDetails] = useState<ChapterData | null>(chapter)
	const [refusal, setRefusal] = useState<TextFixRefusal | null>(null)

	useEffect(() => {
		if (!opened || !chapter?.id) return

		setThumbnailPreview(null)
		setThumbnailProgress(0)
		setDocProgress(0)
		setUploadedDoc(null)
		setDetails(chapter)
		setRefusal(null)

		let cancelled = false

		const loadChapter = async () => {
			setLoading(true)
			try {
				const res = await getChapterDetails(chapter.id)
				const details = parseChapterDetails(res) ?? chapter
				if (cancelled) return
				const nextForm = buildFormFromChapter(details)
				setForm(nextForm)
				setBaselineForm(nextForm)
				setDetails(details)
			} catch (error) {
				if (cancelled) return
				const nextForm = buildFormFromChapter(chapter)
				setForm(nextForm)
				setBaselineForm(nextForm)
				openError(error)
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		void loadChapter()

		return () => {
			cancelled = true
		}
	}, [opened, chapter?.id, chapter, openError])

	const bookCoverUrl = getBookCoverImageUrl(book)
	const bookTitle = getBookTitle(book)
	const normalizedBookTitle = bookTitle.slice(0, TITLE_MAX)
	const hasThumbnail = Boolean(form.thumbnail || thumbnailPreview)
	const isUsingBookCover = Boolean(
		bookCoverUrl && form.thumbnail === bookCoverUrl && !thumbnailPreview,
	)
	const isUsingBookTitle = Boolean(bookTitle && form.title === normalizedBookTitle)

	const hasChanges = useMemo(() => {
		return (
			form.title !== baselineForm.title ||
			form.summary !== baselineForm.summary ||
			form.thumbnail !== baselineForm.thumbnail ||
			form.docUrl !== baselineForm.docUrl
		)
	}, [baselineForm, form])

	const handleRemoveThumbnail = () => {
		setForm((prev) => ({ ...prev, thumbnail: '' }))
		setThumbnailPreview(null)
	}

	const handleUseBookCover = () => {
		if (!bookCoverUrl) {
			openError(new Error('This book has no cover image'))
			return
		}

		setForm((prev) => ({ ...prev, thumbnail: bookCoverUrl }))
		setThumbnailPreview(null)
	}

	const handleUseBookTitle = () => {
		if (!bookTitle) {
			openError(new Error('This book has no title'))
			return
		}

		setForm((prev) => ({ ...prev, title: normalizedBookTitle }))
	}

	const handleUploadThumbnail = async (files: File[]) => {
		const file = files[0]
		if (!file) return

		setThumbnailPreview(URL.createObjectURL(file))
		setThumbnailProgress(0)

		try {
			const imageUrl = await uploadProgress(file, (event) => {
				const { loaded, total } = event
				if (total) {
					setThumbnailProgress(Math.round((loaded * 100) / total))
				}
			})
			setForm((prev) => ({ ...prev, thumbnail: imageUrl }))
		} catch (error) {
			openError(error)
		}
	}

	const handleUploadDoc = async (files: File[]) => {
		const file = pickDocWordFiles(files)[0]
		if (!file) {
			openError(new Error('Only .doc or .docx files are allowed'))
			return
		}

		setDocProgress(0)
		setRefusal(null)
		try {
			const docUrl = await uploadDocFile(file, (event) => {
				const { loaded, total } = event
				if (total) {
					setDocProgress(Math.round((loaded * 100) / total))
				}
			})

			if (!docUrl) {
				openError(new Error('Upload failed — no URL returned'))
				return
			}

			setForm((prev) => ({ ...prev, docUrl }))
			setUploadedDoc({ url: docUrl, name: file.name })
		} catch (error) {
			if (!axios.isCancel(error)) {
				openError(error)
			}
		}
	}

	const handleRejectDoc = (files: File[]) => {
		const docFiles = pickDocWordFiles(files)
		if (docFiles.length) {
			void handleUploadDoc(docFiles)
			return
		}

		openError(new Error('Only .doc or .docx files are allowed'))
	}

	const docReplaced = Boolean(form.docUrl) && form.docUrl !== baselineForm.docUrl
	const currentWordFile = getChapterWordFile(details, book)
	const docName =
		uploadedDoc?.url === form.docUrl
			? uploadedDoc.name
			: currentWordFile.url === form.docUrl && currentWordFile.nameRecorded
				? currentWordFile.name
				: null

	// UD-614: a new Word file saved from this drawer replaces the file the one way the CMS has:
	// the chapter stays published, audio and translations are kept, and the server refuses a file
	// with another number of pages or paragraphs. Without a new file the request is what it
	// always was.
	const handleSave = async () => {
		if (!chapter?.id) return

		const title = form.title.trim()

		setSaving(true)
		try {
			const payload = {
				title: title || null,
				summary: form.summary.trim(),
				thumbnail: form.thumbnail || null,
				doc_url: form.docUrl || null,
				...(docReplaced
					? {
							replace_mode: 'text_fix' as const,
							...(uploadedDoc?.url === form.docUrl
								? { doc_file_name: uploadedDoc.name }
								: {}),
						}
					: {}),
			}

			const res = await updatePublishedChapter(chapter.id, payload)
			if (res?.data?.code === 200 || res?.status === 200) {
				const result = getTextFixResult(res)
				if (docReplaced && isTextFixPartial(result)) {
					// Never only "updated" when a change of the new file was left out
					openModal({ type: 'warning', label: describeTextFixResult(result) })
				} else {
					openModal({
						type: 'success',
						label: docReplaced
							? describeTextFixResult(result)
							: 'Chapter updated successfully',
					})
				}
				await onRefresh?.()
				onClose()
			}
		} catch (error) {
			const refused = getTextFixRefusal(error)
			if (refused) {
				// Nothing was saved: the reason and the advice are shown beside the file
				setRefusal(refused)
			} else {
				openError(error)
			}
		} finally {
			setSaving(false)
		}
	}

	return (
		<CustomDrawer
			opened={opened}
			onClose={onClose}
			width={640}
			loading={loading || saving}
			title={
				<Stack spacing={4}>
					<Text size="xs" color="dimmed">
						UniVini · Edit a chapter
					</Text>
					<Text weight={700}>{book?.title || '-'}</Text>
					<Text size="sm" color="dimmed">
						Update chapter information and English DOCX file
					</Text>
				</Stack>
			}
			footer={
				<CustomButton
					fullWidth
					onClick={handleSave}
					disabled={!hasChanges || saving}
					sx={{
						height: 44,
						border: 'none',
						color: '#fff',
						background: hasChanges ? '#1f7a44' : '#cbd5e1',
						':hover': {
							background: hasChanges ? '#195c34 !important' : '#cbd5e1',
							color: '#fff',
						},
					}}
				>
					Save
				</CustomButton>
			}
		>
			<Stack spacing={20} sx={{ padding: 16 }}>
				<Text size="sm" color="dimmed">
					If your book has no chapters, just upload DOCX file.
				</Text>

				<Stack spacing={8}>
					<Text
						size="xs"
						weight={700}
						sx={{ textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.4 }}
					>
						Chapter thumbnail (optional)
					</Text>
					{thumbnailProgress > 0 && thumbnailProgress < 100 && (
						<CustomProgress value={thumbnailProgress} />
					)}
					{hasThumbnail && (
						<BoxWithRemove onRemove={handleRemoveThumbnail}>
							<Image
								src={thumbnailPreview || form.thumbnail}
								alt="Chapter thumbnail"
								height={180}
								width="auto"
								fit="contain"
								sx={{ maxWidth: '100%' }}
							/>
						</BoxWithRemove>
					)}
					<Dropzone
						accept={IMAGE_MIME_TYPE}
						onDrop={handleUploadThumbnail}
						multiple={false}
						activateOnClick={!hasThumbnail}
						sx={{
							border: '1px dashed #dbe6dc',
							borderRadius: 12,
							background: '#fafcfa',
							position: 'relative',
						}}
					>
						<Text size="sm" color="dimmed" align="center">
							{hasThumbnail
								? 'Drag and drop to replace thumbnail'
								: 'Drag and drop thumbnail or click to browse'}
						</Text>
					</Dropzone>
					<CustomButton
						variant="outlined"
						onClick={handleUseBookCover}
						disabled={!bookCoverUrl || isUsingBookCover}
						sx={{ height: 36, alignSelf: 'flex-start' }}
					>
						{isUsingBookCover ? 'Using book cover image' : 'Use book cover image'}
					</CustomButton>
				</Stack>

				<Stack spacing={6}>
					<CustomInput
						label="Chapter title (optional)"
						value={form.title}
						maxLength={TITLE_MAX}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, title: e.target.value }))
						}
						placeholder="Chapter title"
					/>
					<Flex justify="space-between" align="center">
						<CustomButton
							variant="outlined"
							onClick={handleUseBookTitle}
							disabled={!bookTitle || isUsingBookTitle}
							sx={{ height: 36 }}
						>
							{isUsingBookTitle ? 'Using book title' : 'Use book title'}
						</CustomButton>
						<Text size="xs" color="dimmed">
							{form.title.length}/{TITLE_MAX}
						</Text>
					</Flex>
				</Stack>

				<Stack spacing={6}>
					<CustomInput
						label="Chapter summary"
						value={form.summary}
						maxLength={SUMMARY_MAX}
						onChange={(e) =>
							setForm((prev) => ({ ...prev, summary: e.target.value }))
						}
						placeholder="Describe"
						multiline
						rows={4}
					/>
					<Flex justify="flex-end">
						<Text size="xs" color="dimmed">
							{form.summary.length}/{SUMMARY_MAX}
						</Text>
					</Flex>
				</Stack>

				<Stack spacing={12}>
					<Flex justify="space-between" align="center">
						<Text size="sm" weight={700} sx={{ textTransform: 'uppercase' }}>
							Upload files
						</Text>
					</Flex>

					<Text size="sm" weight={600}>
						English
					</Text>

					{docProgress > 0 && docProgress < 100 && (
						<CustomProgress value={docProgress} />
					)}

					{form.docUrl ? (
						<DocFileCard
							url={form.docUrl}
							name={docName}
							onDelete={() => {
								setRefusal(null)
								setForm((prev) => ({ ...prev, docUrl: '' }))
							}}
							showDelete
						/>
					) : (
						<Text size="sm" color="dimmed">
							No English DOCX uploaded
						</Text>
					)}

					{docReplaced && <WordFileReplaceNotice />}
					{refusal && <WordFileRefusalAlert refusal={refusal} />}

					<Dropzone
						accept={DOCX_DROPZONE_ACCEPT}
						onDrop={handleUploadDoc}
						onReject={(rejections) =>
							handleRejectDoc(rejections.map((item) => item.file))
						}
						multiple={false}
						sx={{
							border: '1px dashed #dbe6dc',
							borderRadius: 12,
							background: '#fafcfa',
							padding: 16,
						}}
					>
						<Text size="sm" color="dimmed" align="center">
							Drag and drop DOCX file or click to browse
						</Text>
					</Dropzone>
				</Stack>
			</Stack>
		</CustomDrawer>
	)
}

function BoxWithRemove({
	children,
	onRemove,
}: {
	children: React.ReactNode
	onRemove: () => void
}) {
	return (
		<div style={{ position: 'relative', width: '100%' }}>
			<IconButton
				onMouseDown={(event) => {
					event.preventDefault()
					event.stopPropagation()
				}}
				onClick={(event) => {
					event.preventDefault()
					event.stopPropagation()
					onRemove()
				}}
				size="small"
				aria-label="Remove thumbnail"
				sx={{
					position: 'absolute',
					top: 8,
					right: 8,
					zIndex: 2,
					background: '#fff',
					boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
				}}
			>
				<IconX size={16} />
			</IconButton>
			{children}
		</div>
	)
}

export default ChapterInfoEditDrawer
